import { FC, useMemo, useCallback, useState } from 'react';
import { ScheduledCall } from '@teachur/http-client';
import {
  generateCalendarDays,
  formatMonthYear,
  isSameDay,
} from '@new/features/schedule/lib/helpers';
import { timestampToDate } from '@new/common/lib/dateTime';
import { CalendarAgendaMonthView } from './CalendarAgendaMonthView';
import { AgendaList } from './AgendaList';

interface CalendarAgendaProps {
  scheduledCalls: ScheduledCall[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onJoinCall: (videoCallId: string) => void;
}

export const CalendarAgenda: FC<CalendarAgendaProps> = ({
  scheduledCalls,
  selectedDate,
  onDateChange,
  onJoinCall,
}) => {
  const [currentMonth, setCurrentMonth] = useState(selectedDate);
  const today = useMemo(() => new Date(), []);
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const days = useMemo(() => generateCalendarDays(year, month), [year, month]);
  const monthYearLabel = formatMonthYear(currentMonth);

  const datesWithCalls = useMemo(() => {
    const dateSet = new Set<string>();
    scheduledCalls.forEach((call) => {
      const callDate = timestampToDate(call.startTime);
      const dateKey = `${callDate.getFullYear()}-${callDate.getMonth()}-${callDate.getDate()}`;
      dateSet.add(dateKey);
    });
    return dateSet;
  }, [scheduledCalls]);

  const hasCallsOnDate = useCallback(
    (date: Date | null): boolean => {
      if (!date) return false;
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      return datesWithCalls.has(dateKey);
    },
    [datesWithCalls]
  );

  const handleMonthChange = useCallback(
    (direction: 'prev' | 'next') => {
      const monthOffset = direction === 'prev' ? -1 : 1;
      const newMonth = new Date(year, month + monthOffset, 1);
      setCurrentMonth(newMonth);
    },
    [year, month]
  );

  const dayScheduledCalls = useMemo(() => {
    return scheduledCalls.filter((call) => {
      const callDate = timestampToDate(call.startTime);
      return isSameDay(callDate, selectedDate);
    });
  }, [scheduledCalls, selectedDate]);

  return (
    <div className="w-full shadow-sm flex flex-col gap-6 bg-(--neutral-white-NW1000) rounded-xl border border-neutral-200 p-6">
      <CalendarAgendaMonthView
        monthYearLabel={monthYearLabel}
        days={days}
        selectedDay={selectedDate}
        today={today}
        hasSlots={hasCallsOnDate}
        onDayClick={onDateChange}
        onMonthChange={handleMonthChange}
        showAllDates
      />

      <div className="flex flex-col gap-1">
        <h4 className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-l4)] font-medium text-(--neutral-black-NB500)! m-0!">
          Approaching deadlines
        </h4>
        <AgendaList scheduledCalls={dayScheduledCalls} onJoinCall={onJoinCall} />
      </div>
    </div>
  );
};
